import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Switch,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudIcon from '@mui/icons-material/Cloud';
import InfoIcon from '@mui/icons-material/Info';
import { IconRefresh, IconPlus } from '@tabler/icons-react';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton } from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip } from '../components/chip';
import type {
  Process,
  CreateProcessRequest,
  UpdateProcessRequest,
} from '@techgitter/contracts';
import {
  getProcesses,
  createProcess,
  updateProcess,
  deleteProcess,
} from '../services/processesApi';
import { ProcessDialog } from '../components/admin/ProcessDialog';

export const ProcessesAdminPage: React.FC = () => {
  const [processes, setProcesses] = useState<Process[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProcess, setEditingProcess] = useState<Process | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadProcesses = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getProcesses(signal);
      setProcesses(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load processes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadProcesses(controller.signal);
    return () => controller.abort();
  }, [loadProcesses]);

  const handleCreate = () => {
    setEditingProcess(null);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleEdit = (process: Process) => {
    setEditingProcess(process);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleSave = async (payload: CreateProcessRequest | UpdateProcessRequest) => {
    setIsSaving(true);
    setDialogError(null);

    try {
      if (editingProcess) {
        await updateProcess(editingProcess.id, payload);
      } else {
        await createProcess(payload as CreateProcessRequest);
      }
      setDialogOpen(false);
      setEditingProcess(null);
      setSnackbar({
        open: true,
        message: editingProcess ? 'Process updated successfully' : 'Process created successfully',
        severity: 'success',
      });
      loadProcesses();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (process: Process) => {
    const previousState = process.isActive;
    setProcesses((current) =>
      current.map((p) => (p.id === process.id ? { ...p, isActive: !p.isActive } : p))
    );

    try {
      await updateProcess(process.id, { isActive: !previousState });
    } catch (err) {
      setProcesses((current) =>
        current.map((p) => (p.id === process.id ? { ...p, isActive: previousState } : p))
      );
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to update',
        severity: 'error',
      });
    }
  };

  const handleToggleReportable = async (process: Process) => {
    const previousState = process.isReportableOnEmail;
    setProcesses((current) =>
      current.map((p) => (p.id === process.id ? { ...p, isReportableOnEmail: !p.isReportableOnEmail } : p))
    );

    try {
      await updateProcess(process.id, { isReportableOnEmail: !previousState });
    } catch (err) {
      setProcesses((current) =>
        current.map((p) => (p.id === process.id ? { ...p, isReportableOnEmail: previousState } : p))
      );
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to update',
        severity: 'error',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    setIsDeleting(true);
    try {
      await deleteProcess(deleteConfirmId);
      setProcesses((current) => current.filter((p) => p.id !== deleteConfirmId));
      setDeleteConfirmId(null);
      setSnackbar({ open: true, message: 'Process deleted', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to delete',
        severity: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteTarget = processes.find((p) => p.id === deleteConfirmId);

  return (
    <>
      <SecondaryToolbar floating>
          <ToolbarSpacer />
          <ToolbarButton icon={IconRefresh} onClick={() => loadProcesses()}>
            Refresh
          </ToolbarButton>
          <ToolbarButton variant="primary" icon={IconPlus} onClick={handleCreate}>
            Add Process
          </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell>Name</DataTableHeadCell>
                <DataTableHeadCell>Category</DataTableHeadCell>
                <DataTableHeadCell>Description</DataTableHeadCell>
                <DataTableHeadCell align="center">Links</DataTableHeadCell>
                <DataTableHeadCell align="center">Email Report</DataTableHeadCell>
                <DataTableHeadCell align="center">Active</DataTableHeadCell>
                <DataTableHeadCell align="right">Actions</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {processes.length === 0 ? (
                <DataTableEmpty colSpan={7}>No processes configured</DataTableEmpty>
              ) : (
                processes.map((process) => (
                  <DataTableRow key={process.id}>
                    <DataTableCell bold>{process.name}</DataTableCell>
                    <DataTableCell>
                      {process.category ? <Chip>{process.category}</Chip> : '—'}
                    </DataTableCell>
                    <DataTableCell>
                      <Tooltip title={process.description ?? ''}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 288, fontSize: 13 }}>
                          {process.description || '—'}
                        </Typography>
                      </Tooltip>
                    </DataTableCell>
                    <DataTableCell align="center">
                      {process.urlAzure && (
                        <Tooltip title="Open in Azure">
                          <IconButton size="small" component="a" href={process.urlAzure} target="_blank" rel="noopener noreferrer">
                            <CloudIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {process.urlInfo && (
                        <Tooltip title="Open info / docs">
                          <IconButton size="small" component="a" href={process.urlInfo} target="_blank" rel="noopener noreferrer">
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!process.urlAzure && !process.urlInfo && '—'}
                    </DataTableCell>
                    <DataTableCell align="center">
                      <Switch
                        checked={process.isReportableOnEmail}
                        onChange={() => handleToggleReportable(process)}
                        size="small"
                      />
                    </DataTableCell>
                    <DataTableCell align="center">
                      <Switch
                        checked={process.isActive}
                        onChange={() => handleToggleActive(process)}
                        size="small"
                      />
                    </DataTableCell>
                    <DataTableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(process)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteConfirmId(process.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </DataTableCell>
                  </DataTableRow>
                ))
              )}
            </tbody>
          </DataTable>
        </DataTableCard>
      )}
      </Box>

      <Dialog
        open={deleteConfirmId !== null}
        onClose={isDeleting ? undefined : () => setDeleteConfirmId(null)}
      >
        <DialogTitle>Delete Process</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.
            Any scheduled functions referencing it will be unlinked.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <ProcessDialog
        open={dialogOpen}
        process={editingProcess}
        isSaving={isSaving}
        error={dialogError}
        onClose={() => {
          if (!isSaving) {
            setDialogOpen(false);
            setEditingProcess(null);
            setDialogError(null);
          }
        }}
        onSave={handleSave}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
